// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { OutwriteShadesBrandColor } from '@outwrite/themes'
import type { ActionTypes, I18nextKey } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

// Others
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import AcknowledgementOfCountry from '../components/localized/AcknowledgementOfCountry'

import {
  HeroSection,
  Title as HeroTitle,
  Description as HeroDescription
} from '../components/Hero'
import { Section, Description, Item, FlexItem } from '../components/Section'
import { AltFlexSectionEven, AltFlexSectionOdd, Title as AltTitle } from '../components/AltSection'
import { CenteredSection, Title as CenteredTitle, Subtitle, SpacingContainer } from '../components/CenteredSection'
import { StatDivider, StatNumbers, StatText, StatWrapper } from '../components/Stats'

import PrimaryAction from '../components/PrimaryAction'
import InlineLink from '../components/InlineLink'
import Highlight from '../components/Highlight'
import FeatureGrid from '../components/FeatureGrid'

import BottomCta from '../components/BottomCta'

import {
  defaultPageSpecificSEO,
  pageSpecificCtaLink,
  HELP_EMAIL_LINK,
  MEDIA_EMAIL_LINK
} from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'about' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, about: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type AboutKey = I18nextKey<typeof import ('../../public/locales/en/about.json')>
interface FlexSectionContent extends Record<'titleKey' | 'descriptionKey', AboutKey> {
  img: { src: any } & Record<'altKey', AboutKey>
  color: keyof OutwriteShadesBrandColor
}

interface StatsContent {
  count: number | string
  i18nKey: AboutKey
  color: keyof OutwriteShadesBrandColor
}

interface TeamContent extends Record<'nameKey' | 'roleKey', AboutKey>{
  imgSrc: any
}

/**
 * Mapping json keys for content structure
 */

const SECTIONS: FlexSectionContent[] = [
  {

    titleKey: 'sections.idea.TITLE',
    descriptionKey: 'sections.idea.DESCRIPTION',
    img: {
      src: require('../../public/assets/nick-and-craig.png'),
      altKey: 'sections.idea.NICK_CRAIG_IMG_ALT'
    },
    color: 'purple'
  },
  {
    titleKey: 'sections.ai.TITLE',
    descriptionKey: 'sections.ai.DESCRIPTION',
    img: {
      src: require('../../public/assets/gradeproof-beginnings.jpg'),
      altKey: 'sections.ai.GRADEPROOF_IMG_ALT'
    },
    color: 'yellow'
  },
  {
    titleKey: 'sections.team.TITLE',
    descriptionKey: 'sections.team.DESCRIPTION',
    img: {
      src: require('../../public/assets/team-photo.jpg'),
      altKey: 'sections.team.TEAM_PHOTO_IMG_ALT'
    },
    color: 'purple'
  }
]

const STATS: StatsContent[] = [
  { count: '1,000,000', i18nKey: 'stats.USER_COUNT', color: 'yellow' },
  { count: '211', i18nKey: 'stats.COUNTRY_COUNT', color: 'purple' },
  { count: '50,000,000', i18nKey: 'stats.DOCUMENT_COUNT', color: 'red' }
]

const THE_TEAM: TeamContent[] = [
  { nameKey: 'team.members.theo.NAME', roleKey: 'team.members.theo.ROLE', imgSrc: require('../../public/assets/team/theo.jpg') },
  { nameKey: 'team.members.nick.NAME', roleKey: 'team.members.nick.ROLE', imgSrc: require('../../public/assets/team/nick.jpg') },
  { nameKey: 'team.members.julie.NAME', roleKey: 'team.members.julie.ROLE', imgSrc: require('../../public/assets/team/julie.jpg') },
  { nameKey: 'team.members.nadine.NAME', roleKey: 'team.members.nadine.ROLE', imgSrc: require('../../public/assets/team/nadine.jpg') },
  { nameKey: 'team.members.craig.NAME', roleKey: 'team.members.craig.ROLE', imgSrc: require('../../public/assets/team/craig.jpg') },
  { nameKey: 'team.members.osama.NAME', roleKey: 'team.members.osama.ROLE', imgSrc: require('../../public/assets/team/osama.jpg') }
]

const About: NextPage = () => {
  const router = useRouter()
  const { t } = useTranslation('about')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      <Header t={tCommon} action={ctaAction} data-sticky={scrolledPassed} />

      {/* Hero */}
      <HeroSection>
        <HeroTitle>
          <Trans t={t} i18nKey='hero.TITLE'>
            We&apos;re on a mission to help <Highlight color='yellow'>anyone</Highlight> become a <Highlight color='purple'>powerful writer</Highlight>.
          </Trans>
        </HeroTitle>
        <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
        <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
      </HeroSection>

      <Section bgColor='gray'>
        {/* Sections */}
        {SECTIONS.map(({ img, titleKey, descriptionKey, color }, i) => {
          const Wrapper = i % 2 ? AltFlexSectionEven : AltFlexSectionOdd
          return (
            <Wrapper
              key={i}
              bgColor={color}
            >
              <Item>
                <Image
                  src={img.src}
                  alt={t(img.altKey) as string}
                  height={312}
                  width={467}
                />
              </Item>
              <FlexItem>
                <AltTitle>{t(titleKey)}</AltTitle>
                <Description>{t(descriptionKey)}</Description>
              </FlexItem>
            </Wrapper>
          )
        })}

        {/* Stats */}
        <SpacingContainer>
          <CenteredTitle>{t('stats.TITLE')}</CenteredTitle>
        </SpacingContainer>
        <StatWrapper>
          {STATS.map(({ count, i18nKey, color }) => (
            <StatDivider key={i18nKey}>
              <Trans
                t={t}
                i18nKey={i18nKey}
                values={{ count }}
                components={[
                  <StatNumbers color={color} key={0} />,
                  <StatText key={1} />
                ]}
              />
            </StatDivider>
          ))}
        </StatWrapper>
      </Section>

      {/* The team <3 */}
      <CenteredSection>
        <CenteredTitle>{t('team.TITLE')}</CenteredTitle>
        <Subtitle>{t('team.DESCRIPTION')}</Subtitle>
        <FeatureGrid
          cols={4}
          responsiveCols={{ tablet: 2, mobile: 2 }}
          list={THE_TEAM.map(({ nameKey, roleKey, imgSrc }) => ({
            title: t(nameKey) as string,
            description: t(roleKey) as string,
            img: { src: imgSrc, alt: t(nameKey) as string }
          }))}
        />
      </CenteredSection>

      <AcknowledgementOfCountry t={tCommon} />

      {/* BottomCta */}
      <BottomCta
        action={ctaAction}
        {...t('BOTTOM_CTA')}
        bgColor='gray'
        description={
          <Trans
            t={t}
            i18nKey='BOTTOM_CTA.description'
            components={[
              <InlineLink {...HELP_EMAIL_LINK} key={0} />,
              <InlineLink {...MEDIA_EMAIL_LINK} key={1} />
            ]}
            defaults='For general enquiries, contact <0>help@outwrite.com</0>.<br /><br />For press and partnership enquiries, contact us at <1>media@outwrite.com</1>'
          />
        }
      />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default About
